/**
 * Prebuilt landing layouts for conference and expo event sites:
 * hero-forward conference sites, expo floors, galas, and workshop tracks.
 */

function uid(prefix = "b") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function assignBlockIds(blocks) {
  return blocks.map((b) => ({ ...b, id: uid("b") }));
}

/** @param {{ title?: string; slug?: string; cover?: string; startDate?: string; endDate?: string; venue?: string }} event */
export function buildBlocksFromTemplate(templateId, event) {
  const title = event?.title || "Your event";
  const venue = event?.venue || "Venue TBA";
  const dates =
    event?.startDate && event?.endDate
      ? `${event.startDate} — ${event.endDate}`
      : "Dates TBA";

  const factories = {
    "conference-classic": () => [
      {
        id: "x",
        type: "hero",
        visible: true,
        props: {
          eyebrow: dates,
          title,
          titleAccent: "",
          body: "Keynotes, breakouts, and curated networking. Register before tiers sell out.",
          cta: "Register now",
          bg: "image",
          imageUrl: "",
        },
      },
      { id: "x", type: "metrics", visible: true, props: {} },
      {
        id: "x",
        type: "schedule",
        visible: true,
        props: { eyebrow: "Agenda", title: "Program at a glance" },
      },
      {
        id: "x",
        type: "speakers",
        visible: true,
        props: { eyebrow: "Speakers", title: "Featured voices" },
      },
      {
        id: "x",
        type: "sponsors",
        visible: true,
        props: { caption: "Presented with our partners" },
      },
      {
        id: "x",
        type: "tickets",
        visible: true,
        props: {},
      },
      {
        id: "x",
        type: "faq",
        visible: true,
        props: {
          items: [
            { q: "Is there a virtual option?", a: "Streaming access is listed per ticket tier where available." },
            { q: "Can I bring a colleague?", a: "Group rates appear at checkout when enabled by the organiser." },
          ],
        },
      },
      {
        id: "x",
        type: "cta",
        visible: true,
        props: { title: "Save your spot.", cta: `Join us · ${venue}` },
      },
    ],

    "trade-show-floor": () => [
      {
        id: "x",
        type: "hero",
        visible: true,
        props: {
          eyebrow: `${venue} · Expo`,
          title: `${title}`,
          titleAccent: " / Show floor",
          body: "Booth demos, product theatres, and buyer meetings—built for pipeline and partnerships.",
          cta: "Book meetings",
          bg: "image",
          imageUrl: "",
        },
      },
      {
        id: "x",
        type: "sponsors",
        visible: true,
        props: { caption: "Exhibitors & sponsors" },
      },
      {
        id: "x",
        type: "metrics",
        visible: true,
        props: {},
      },
      {
        id: "x",
        type: "tickets",
        visible: true,
        props: {},
      },
      {
        id: "x",
        type: "map",
        visible: true,
        props: { address: `${venue}`, embedUrl: "" },
      },
      {
        id: "x",
        type: "schedule",
        visible: true,
        props: { eyebrow: "Floor sessions", title: "Theatre & spotlight talks" },
      },
      {
        id: "x",
        type: "faq",
        visible: true,
        props: {
          items: [
            { q: "Lead retrieval?", a: "Enable badge scans from your exhibitor portal where configured." },
            { q: "Move-in times?", a: "Check the logistics email from your organiser for dock schedules." },
          ],
        },
      },
      {
        id: "x",
        type: "cta",
        visible: true,
        props: { title: "See you on the floor.", cta: "Get exhibitor access" },
      },
    ],

    "gala-awards": () => [
      {
        id: "x",
        type: "hero",
        visible: true,
        props: {
          eyebrow: "An evening to celebrate",
          title,
          titleAccent: "",
          body: "Black-tie optional. Dinner, live awards, and a headline performance.",
          cta: "Reserve seats",
          bg: "image",
          imageUrl: "",
        },
      },
      {
        id: "x",
        type: "quote",
        visible: true,
        props: {
          quote: "Last year set the bar—this year we raise it again.",
          author: "Host committee",
        },
      },
      {
        id: "x",
        type: "speakers",
        visible: true,
        props: { eyebrow: "Honorees & hosts", title: "The evening's voices" },
      },
      {
        id: "x",
        type: "gallery",
        visible: true,
        props: { images: event?.cover ? [event.cover] : [] },
      },
      {
        id: "x",
        type: "tickets",
        visible: true,
        props: {},
      },
      {
        id: "x",
        type: "text",
        visible: true,
        props: {
          title: "Table pricing & sponsorship",
          body: "Premium tables include programme recognition. Ask your team about naming opportunities.",
        },
      },
      {
        id: "x",
        type: "cta",
        visible: true,
        props: { title: "Be part of the night.", cta: "Request a table" },
      },
    ],

    "workshop-series": () => [
      {
        id: "x",
        type: "hero",
        visible: true,
        props: {
          eyebrow: "Hands-on track",
          title,
          titleAccent: " / Workshops",
          body: "Small groups, facilitators, and take-home templates—learn by doing.",
          cta: "Choose a workshop",
          bg: "image",
          imageUrl: "",
        },
      },
      {
        id: "x",
        type: "schedule",
        visible: true,
        props: { eyebrow: "Sessions", title: "Workshop timetable" },
      },
      {
        id: "x",
        type: "accordion",
        visible: true,
        props: {
          title: "What to expect",
          items: [
            { title: "Capacity", body: "Workshops are capped; RSVP locks when full." },
            { title: "Materials", body: "Bring a laptop; worksheets are provided digitally." },
            { title: "Prerequisites", body: "Beginner-friendly unless marked intermediate in the agenda." },
          ],
        },
      },
      {
        id: "x",
        type: "speakers",
        visible: true,
        props: { eyebrow: "Facilitators", title: "Your instructors" },
      },
      {
        id: "x",
        type: "tickets",
        visible: true,
        props: {},
      },
      {
        id: "x",
        type: "newsletter",
        visible: true,
        props: {},
      },
    ],

    "networking-mixer": () => [
      {
        id: "x",
        type: "hero",
        visible: true,
        props: {
          eyebrow: `${dates}`,
          title,
          titleAccent: " · Mixer",
          body: "Curated intros, industry circles, and an open floor for meaningful conversations.",
          cta: "RSVP",
          bg: "image",
          imageUrl: "",
        },
      },
      { id: "x", type: "metrics", visible: true, props: {} },
      {
        id: "x",
        type: "text",
        visible: true,
        props: {
          title: "Format",
          body: "Short welcomes, rotating circles, and optional 1:1 matching based on your profile.",
        },
      },
      {
        id: "x",
        type: "map",
        visible: true,
        props: { address: venue, embedUrl: "" },
      },
      {
        id: "x",
        type: "newsletter",
        visible: true,
        props: {},
      },
      {
        id: "x",
        type: "cta",
        visible: true,
        props: { title: "Save the date.", cta: "I'm attending" },
      },
    ],

    blank: () => [
      {
        id: "x",
        type: "hero",
        visible: true,
        props: {
          eyebrow: dates,
          title,
          titleAccent: "",
          body: "Start from a blank canvas—add blocks from the library to build your page.",
          cta: "Get tickets",
          bg: "image",
          imageUrl: "",
        },
      },
      {
        id: "x",
        type: "text",
        visible: true,
        props: {
          title: "Build your story",
          body: "Drag sections in the outline to reorder. Click any block to edit copy and layout props.",
        },
      },
    ],
  };

  const fn = factories[templateId] || factories.blank;
  return assignBlockIds(fn());
}

export const PAGE_BUILDER_TEMPLATES = [
  {
    id: "conference-classic",
    name: "Summit & keynotes",
    blurb: "Full conference arc: agenda, speakers, sponsors, tickets—like a flagship Cvent microsite.",
    category: "Conference",
    themeName: "Aivory Default",
    preview: "linear-gradient(135deg,#0c0c10 0%,#1a1a22 50%,#252018 100%)",
    accent: "#FF5608",
  },
  {
    id: "trade-show-floor",
    name: "Expo & show floor",
    blurb: "Exhibitor-forward layout: sponsor strip, floor map, theatre sessions—trade show style.",
    category: "Expo",
    themeName: "Ice Blue",
    preview: "linear-gradient(135deg,#080910 0%,#0f1525 55%,#12182a 100%)",
    accent: "#60A5FA",
  },
  {
    id: "gala-awards",
    name: "Gala & awards",
    blurb: "Evening event: quote, honorees, gallery, tables—black-tie gala pacing.",
    category: "Gala",
    themeName: "Editorial Cream",
    preview: "linear-gradient(135deg,#141210 0%,#1f1c18 45%,#2a2620 100%)",
    accent: "#FAFAFA",
  },
  {
    id: "workshop-series",
    name: "Workshop track",
    blurb: "Education-heavy: timetable, accordion FAQs, facilitators, newsletter capture.",
    category: "Workshops",
    themeName: "Terracotta",
    preview: "linear-gradient(135deg,#0f0a08 0%,#1c1410 50%,#261a14 100%)",
    accent: "#E25D3A",
  },
  {
    id: "networking-mixer",
    name: "Networking mixer",
    blurb: "RSVP-first mixer with venue and updates—lighter than a full multi-day site.",
    category: "Networking",
    themeName: "Ice Blue",
    preview: "linear-gradient(135deg,#07090e 0%,#121826 100%)",
    accent: "#60A5FA",
  },
  {
    id: "blank",
    name: "Blank canvas",
    blurb: "Hero + placeholder copy only—full Squarespace-style control from scratch.",
    category: "Start fresh",
    themeName: "Aivory Default",
    preview: "linear-gradient(135deg,#09090b 0%,#18181b 100%)",
    accent: "#71717A",
  },
];

export function getTemplateMeta(templateId) {
  if (templateId == null || templateId === "") {
    return {
      id: null,
      name: "Custom layout",
      blurb: "Your page isn’t tied to a preset.",
      category: "Custom",
      themeName: null,
      preview: "linear-gradient(135deg,#09090b 0%,#18181b 100%)",
      accent: "#71717A",
    };
  }
  return PAGE_BUILDER_TEMPLATES.find((t) => t.id === templateId) || PAGE_BUILDER_TEMPLATES.find((t) => t.id === "blank");
}
